import { PopupButton } from '@typeform/embed-react'
import clsx from 'clsx'
import Image from 'next/image'
import { FC, useEffect, useRef, useState } from 'react'

import { Section } from '~/components/common/section'
import { Container } from '~/components/layout/container'
import { Button, ButtonLink } from '~/components/primitives/button'
import { TitleAndSubtitle } from '~/components/primitives/texts'
import individual from '~/public/images/pricing/individual.svg'
import organization from '~/public/images/pricing/organization.svg'
import team from '~/public/images/pricing/team.svg'

import s from './plans.module.scss'

type Contract = {
  type: string
  icon: any
  description: string | Element
  cta: string
  link?: string
  content: Array<{
    title?: string
    description?: string | Element
    features?: {
      title: string
      items: string[]
    }
  }>
}

export const rtbContracts: Contract[] = [
  {
    type: 'Bus',
    icon: individual,
    description: 'Bus owners earn protocol rewards',
    cta: 'View Contract',
    link: 'https://basescan.org/token/not-yet-deloyed-update-when-deployed',
    content: [
      {
        features: {
          title: 'Initial mint',
          items: [
            '4,545 Buses',
            'Buses will be airdropped retroactively to early users of the protocol',
            'TBD, a sale. All decisions will be community driven.',
            'Revenue generated from gameplay is returned to Bus owners via the Bus Staking Pool.'
          ]
        }
      },
      {
        title:
          'Owning a Bus gives you exclusive membership into the Tokenized Transit Union',
        description:
          'The Union is an alliance of bus owners who must ardue the treacherous conditions, put their drivers and heavy duty machinery in harms way, to transport our degen passengers.'
      },
      {
        title: 'Protocol generated revenue goes to Bus owners',
        description:
          'Bus owners can own and stake multiple buses. The more buses you own, the more revenue you earn.'
      },
      {
        title: 'A Bus derives its value from the health of the protocol',
        description:
          'Directly correlated to the popularity of riding the bus, it has real utility that can be viewed and tracked on chain.'
      }
    ]
  },
  {
    type: 'Bus Ticket',
    icon: team,
    description: 'An ERC1155 NFT that can be redeemed for a seat on a bus.',
    cta: 'View Contract',
    link: 'https://basescan.org/token/not-yet-deloyed-update-when-deployed',
    content: [
      {
        title: 'The only tickets for sale by the Station are Level One tickets',
        description:
          'The only way to buy a <i>Bus Ticket</i> (aside from secondary markets like OpenSea), is The Station'
      },
      {
        title: 'Level One Bus Tickets cost 0.002 ETH each',
        description:
          'Earn 5% of sales when you refer users via our affiliate program'
      },
      {
        title: 'Higher level Bus Tickets are earned by playing the game',
        description:
          'Advancing rounds earns higher level Bus Tickets which can be used to continue playing the game or sold on secondary marketplaces'
      }
    ]
  },
  {
    type: 'Ticket Stub',
    icon: organization,
    description: 'ERC721 NFT that can be redeemed for a seat on a bus.',
    cta: 'View Contract',
    link: 'https://basescan.org/token/not-yet-deloyed-update-when-deployed',
    content: [
      {
        features: {
          title: 'When you board a bus, you go through the process of',
          items: [
            'Choosing a route',
            'Burning a Bus Ticket',
            'Receiving a Ticket Stub with verification of your seat on the bus'
          ]
        }
      },
      {
        title: 'Multiple Teams',
        description:
          'Create multiple teams for different workflows, environments and teams within your Organization. We set an Org-wide cap at 10k recordings / month and up to 100 users / developers.'
      },
      {
        title: 'Test Suites',
        description:
          'We offer custom integrations with your e2e test runner and CI/CD pipeline e.g. Playwright, Cypress. Add Replay to CI, upload Replay for test failures to record and debug flaky tests. '
      },
      {
        title: 'Single sign on authentication',
        description: `Replay handles authentication with Google SSO by default. Additional identity providers and authentication protocols of your choice are available e.g Okta.`
      }
    ]
  }
]

const contractTabs = ['Bus', 'Bus Ticket', 'Ticket Stub']
export const Contracts: FC = () => {
  const [activeKey, setActiveKey] = useState(0)
  const [isStuck, setIsStuck] = useState(false)

  const tabsRef = useRef<HTMLDivElement>(null)
  const plansRefs = useRef<(HTMLDivElement | null)[]>([])

  const [scrollPosition, setScrollPosition] = useState(0)

  const handleScroll = () => {
    const position = window.pageYOffset
    setScrollPosition(position)
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    plansRefs.current = Array.from({ length: rtbContracts.length })
  }, [])

  useEffect(() => {
    if (!plansRefs.current) return

    const plans = plansRefs.current

    for (const plan of plans) {
      if (!plan) return

      if (plan?.getBoundingClientRect().top < 400) {
        setActiveKey(plans.indexOf(plan))
      }
    }
  }, [scrollPosition])

  useEffect(() => {
    if (!tabsRef.current) return

    const distanceTop = tabsRef.current.getBoundingClientRect().top

    if (distanceTop < 77) {
      setIsStuck(true)
    } else {
      setIsStuck(false)
    }
  }, [tabsRef, scrollPosition])

  const navigateToPlan = (key: number) => {
    if (!plansRefs.current) return

    const plans = plansRefs.current
    if (!plans[key]) return

    plans[key]?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <Section className={s.section}>
      <TitleAndSubtitle
        title={{
          children: <>Protocol</>,
          hero: true
        }}
        subtitle={{
          children: ''
        }}
      />
      <div
        className={clsx(s['tabs-container'], {
          [s.stuck as string]: isStuck === true
        })}
        ref={tabsRef}
      >
        <Container className={s['inner-container']}>
          {isStuck && <span>Protocol</span>}
          <div className={s.tabs}>
            {contractTabs.map((tab, i) => (
              <button
                onClick={() => navigateToPlan(i)}
                className={clsx({ [s.active as string]: activeKey === i })}
                key={i}
              >
                {tab}
              </button>
            ))}
          </div>
        </Container>
      </div>
      <Container className={s.container}>
        <div className={s['plan-container']}>
          {rtbContracts.map((plan, i) => (
            <div
              key={i}
              ref={(divElement) => (plansRefs.current[i] = divElement)}
              className={clsx(s.plan)}
              id={plan.type}
            >
              <div>
                <Image src={plan.icon} alt={plan.type} />
                <span>{plan.type}</span>
                <span>{plan.description}</span>
                {plan.link ? (
                  <ButtonLink href={plan.link} variant="tertiary-inverted-alt">
                    {plan.cta}
                  </ButtonLink>
                ) : (
                  <PopupButton id="jTudlerL" className={s.cta}>
                    <Button
                      variant="tertiary-inverted-alt"
                      aria-label="Learn more about Test Suites"
                    >
                      {plan.cta}
                    </Button>
                  </PopupButton>
                )}{' '}
              </div>
              <div>
                {plan.content.map((item, i) => (
                  <div key={i}>
                    {item.features && (
                      <>
                        <span>{item.features.title}</span>
                        <ul>
                          {item.features.items.map((feature, i) => (
                            <li key={i}>{feature}</li>
                          ))}
                        </ul>
                      </>
                    )}
                    <span>{item.title}</span>
                    <span>{item.description}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Container>
    </Section>
  )
}
